import { ApiError } from '@kiao-voice/api-client'
import type {
  AuthUser,
  MeResponse as ContractMeResponse,
  OtpRequestResponse,
  OtpVerifyResponse as ContractOtpVerifyResponse,
} from '@kiao-voice/api-contract/auth'
import { getOrCreateDeviceId, readStoredFirstTouch } from '../../lib/identity'
import {
  createWebApiClient,
  createWebPublicApiClient,
  getWebApiClient,
  getWebPublicApiClient,
} from '../../lib/web-api-client'
import { storeToken } from './token-storage'
import { ACTIVE_CONTEXT_FORBIDDEN_ERROR, type OtpPurpose } from '@kiao-voice/core'

// Token + change-event primitives live in token-storage so the shared web API
// client can depend on them without an import cycle (this file's fetchMe goes
// through that client). Re-exported here so `@/features/auth/auth-storage`
// imports keep working unchanged.
export {
  AUTH_TOKEN_CHANGE_EVENT,
  TOKEN_STORAGE_KEY,
  clearStoredToken,
  onAuthTokenChange,
  readStoredToken,
  storeToken,
} from './token-storage'

export class OtpApiError extends Error {
  readonly status: number
  readonly body: unknown
  readonly reason?: string
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `OTP API error ${status}`)
    this.name = 'OtpApiError'
    this.status = status
    this.body = body
    const b = body as { reason?: string } | undefined
    this.reason = b?.reason
  }
}

/**
 * True for a recoverable stale/revoked active-context rejection (403
 * `active_context_forbidden`). The shared client already cleared the dead
 * context, so a caller should RETRY (the next fetch omits the header and the
 * server resolves the smart default) rather than sign the user out — the same
 * contract the batch api-client exposes via its own `isRecoverableActiveContextError`.
 */
export function isRecoverableActiveContextError(err: unknown): boolean {
  return (
    err instanceof OtpApiError &&
    err.status === 403 &&
    (err.body as { error?: string } | undefined)?.error === ACTIVE_CONTEXT_FORBIDDEN_ERROR
  )
}

interface RequestOptions {
  fetchFn?: typeof fetch
  signal?: AbortSignal
}

/** Map a thrown `ApiError` from the shared client to `OtpApiError` (which
 *  re-derives `reason` from the body). Non-ApiError values propagate. */
function toOtpError(error: unknown): unknown {
  if (error instanceof ApiError) {
    const code = (error.body as { error?: string } | null | undefined)?.error
    return new OtpApiError(error.status, error.body, code)
  }
  return error
}

export async function requestOtp(
  email: string,
  purpose: OtpPurpose,
  options: RequestOptions = {}
): Promise<OtpVerifyResponse | null> {
  const client = options.fetchFn ? createWebPublicApiClient(options.fetchFn) : getWebPublicApiClient()
  try {
    const body: OtpRequestResponse = await client.request<OtpRequestResponse>(
      '/api/v1/auth/otp/request',
      {
      method: 'POST',
      body: JSON.stringify({ email, purpose }),
      signal: options.signal,
      }
    )
    if (!body.session) return null
    storeToken(body.session.token.value)
    return body.session
  } catch (error) {
    throw toOtpError(error)
  }
}

/**
 * The OTP verify response IS the canonical contract payload — a session plus
 * `accountWasCreated`, which is what tells a signup conversion apart from a
 * returning sign-in. The flag is optional on the wire, so a caller must treat
 * a missing value as "not known to be a signup" rather than assuming either way.
 */
export type OtpVerifyResponse = ContractOtpVerifyResponse

export async function verifyOtp(
  payload: {
    email: string
    code: string
    purpose: OtpPurpose
    /** Referral code captured at signup (issue #71). Server captures it only on
     *  a brand-new account; ignored on an existing-user sign-in. */
    referralCode?: string
  },
  options: RequestOptions = {}
): Promise<OtpVerifyResponse> {
  const client = options.fetchFn ? createWebPublicApiClient(options.fetchFn) : getWebPublicApiClient()
  let body: OtpVerifyResponse
  try {
    body = await client.request<OtpVerifyResponse>('/api/v1/auth/otp/verify', {
      method: 'POST',
      // This browser's anonymous id, so the server can link the pre-signup
      // funnel to the account this call creates. Omitted when there is none
      // (SSR, cookies blocked); the server treats its absence as ordinary.
      //
      // The first touch rides along for the same reason and is recorded only
      // when this call CREATES the account — a returning visitor who arrives
      // through a later ad must not overwrite where they originally came from,
      // and the server decides that from `accountWasCreated`, not from us. Read
      // from storage rather than captured here: capturing at signup time would
      // record the last click, which is the wrong question.
      body: JSON.stringify({
        ...payload,
        deviceId: getOrCreateDeviceId() || undefined,
        attribution: readStoredFirstTouch() ?? undefined,
      }),
      signal: options.signal,
    })
  } catch (error) {
    throw toOtpError(error)
  }
  // Fires AUTH_TOKEN_CHANGE_EVENT for in-place listeners (navbar, guards).
  storeToken(body.token.value)
  return body
}

/**
 * Web's `/auth/me` payload — the contract-guaranteed {@link AuthUser} core plus
 * the billing column the API's forward-compat `user.serialize()` spread carries
 * along (`serializeUserWithPlan` in client_plan.ts).
 *
 * Named the same as the contract's {@link ContractMeResponse} because it IS
 * that payload; it widens `user` rather than replacing it, so the contract core
 * still governs the shared fields.
 */
export interface MeResponse extends ContractMeResponse {
  user: AuthUser & {
    billingAnchorDay: number | null
  }
}

/**
 * Self-serve account deletion (`DELETE /api/v1/auth/account`) — the App Store
 * 5.1.1(v) requirement, reachable from /app/account on web and deep-linked from
 * the iOS Settings screen.
 *
 * The server deactivates the account, kills every session, and releases the
 * email so it can start a fresh account. Rejects with an `OtpApiError` carrying
 * status 409 and `body.error === 'team_has_members'` when the caller still owns
 * a team with other seats — the caller maps that to a localized explanation.
 */
export async function deleteAccount(options: RequestOptions = {}): Promise<void> {
  const client = options.fetchFn ? createWebApiClient(options.fetchFn) : getWebApiClient()
  try {
    await client.request<{ deleted: true }>('/api/v1/auth/account', {
      method: 'DELETE',
      signal: options.signal,
    })
  } catch (error) {
    throw toOtpError(error)
  }
}

export async function fetchMe(token: string, options: RequestOptions = {}): Promise<MeResponse> {
  // The shared client injects the bearer + X-Active-Context and self-heals:
  //   - 401 (dead token) → clearStoredToken(), guarded by a non-null request
  //     token that still equals the stored one, so a token-less 401 evicts
  //     nothing and a stale in-flight 401 can't evict a token a parallel
  //     sign-in just stored. (We pass the historical `token` arg through for
  //     call-site compatibility; the client reads the live stored token itself.)
  //   - 403 active_context_forbidden / 400 invalid_active_context →
  //     clearActiveContext().
  // The error STILL throws after the heal, so callers keep classifying it
  // (isRecoverableActiveContextError → retry vs surfacing the signed-out
  // state — AuthGuard shows its sign-in placeholder on eviction; it only
  // auto-opens the modal for a visitor who arrived unauthenticated).
  void token
  const client = options.fetchFn ? createWebApiClient(options.fetchFn) : getWebApiClient()
  try {
    return await client.request<MeResponse>('/api/v1/auth/me', {
      method: 'GET',
      signal: options.signal,
    })
  } catch (error) {
    throw toOtpError(error)
  }
}
